import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

// Types
import { SeriesSerializer, CronConfig, Days, ScheduleConfig, SeriesType } from "./types/MeGenTypes";

export type ModCreateSeriesWebsiteProps = {
  open: boolean;
  projectId: number;
  editMode?: boolean;
  viewMode?: boolean;
  editSeries?: SeriesSerializer;
  defaultScheduleConfig?: Partial<CronConfig>;
  defaultCutoffHour?: number;
  onClose: () => void;
  onSave: (data: SeriesSerializer) => void;
};

// --- State Types (แยก Form กับ Schedule) ---
type WebsiteFormState = {
  id?: number;
  name: string;
  series_type: SeriesType;
  rss_urls: string;
  cutoff_hours: number;
};

type ScheduleState = {
  enabled: boolean;
  days: Days[];
  hour: number;
  minute: number;
};

// --- Day Options ---
type DayOption = { value: Days; label: string };
const DAYS_OPTIONS: DayOption[] = [
  { value: "mon", label: "M" },
  { value: "tue", label: "T" },
  { value: "wed", label: "W" },
  { value: "thu", label: "T" },
  { value: "fri", label: "F" },
  { value: "sat", label: "S" },
  { value: "sun", label: "S" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));

// --- Helper: Initial States ---
const getInitialFormState = (
  editMode: boolean,
  viewMode: boolean,
  editSeries: SeriesSerializer | undefined,
  defaultCutoffHour: number
): WebsiteFormState => {
  if ((editMode || viewMode) && editSeries) {
    const cfg = editSeries.schedule_config || {};
    return {
      id: editSeries.id,
      name: editSeries.name,
      series_type: editSeries.series_type,
      rss_urls: cfg.rss_urls?.join("\n") || "",
      cutoff_hours: cfg.cutoff_days ? Math.round(cfg.cutoff_days * 24) : defaultCutoffHour,
    };
  }
  return {
    id: undefined,
    name: "",
    series_type: "rss_news",
    rss_urls: "",
    cutoff_hours: defaultCutoffHour,
  };
};

const getInitialScheduleState = (
  editMode: boolean,
  viewMode: boolean,
  editSeries: SeriesSerializer | undefined,
  defaultCron: Partial<CronConfig>
): ScheduleState => {
  if ((editMode || viewMode) && editSeries) {
    const cfg = editSeries.schedule_config || {};
    return {
      enabled: cfg.cron?.enabled || false,
      days: cfg.cron?.days || (defaultCron.days as Days[]) || ["mon", "tue", "wed", "thu", "fri"],
      hour: cfg.cron?.hour ?? defaultCron.hour ?? 8,
      minute: cfg.cron?.minute ?? defaultCron.minute ?? 30,
    };
  }
  return {
    enabled: false,
    days: (defaultCron.days as Days[]) || ["mon", "tue", "wed", "thu", "fri"],
    hour: defaultCron.hour ?? 8,
    minute: defaultCron.minute ?? 30,
  };
};

// --- Styled Components ---
const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "0.75rem",
    minWidth: "500px",
    maxWidth: "560px",
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1rem 1.5rem",
});

const TitleText = styled(Typography)({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#1e293b",
});

const StyledDialogContent = styled(DialogContent)({
  padding: "1.5rem",
});

const StyledDialogActions = styled(DialogActions)({
  padding: "1rem 1.5rem",
  borderTop: "1px solid #e2e8f0",
  gap: "0.75rem",
});

const FormSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "1.25rem",
});

const FormLabel = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#1e293b",
  marginBottom: "0.5rem",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#e91e63", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

const StyledTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    alignItems: "flex-start",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#e91e63", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

const PeriodSetRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
});

const PeriodInput = styled(TextField)({
  width: "120px",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "0.5rem",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#e91e63", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "10px 14px",
    fontSize: "0.875rem",
    color: "#334155",
  },
});

const NotesList = styled(Box)({
  marginTop: "0.5rem",
});

const NoteItem = styled(Typography)({
  fontSize: "0.75rem",
  color: "#64748b",
  marginBottom: "0.25rem",
  display: "flex",
  alignItems: "flex-start",
  gap: "0.5rem",
  "&::before": { content: '"•"', color: "#94a3b8" },
});

const CheckboxLabel = styled(FormControlLabel)({
  marginRight: "0.25rem",
  "& .MuiFormControlLabel-label": {
    fontSize: "0.8125rem",
    color: "#64748b",
    fontWeight: 500,
  },
  "& .MuiCheckbox-root": {
    padding: "4px",
    color: "#cbd5e1",
    "&.Mui-checked": { color: "#f382a0" },
  },
});

// Inline Schedule Section
const ScheduleInlineBox = styled(Box)({
  backgroundColor: "#fdf2f6",
  border: "1px solid #fce4ec",
  borderRadius: "0.625rem",
  padding: "1rem 1.25rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  marginTop: "0.5rem",
});

const ScheduleSectionLabel = styled(Typography)({
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#64748b",
  marginBottom: "0.5rem",
});

const TimeRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
});

const TimeSeparator = styled(Typography)({
  fontSize: "1.125rem",
  fontWeight: 600,
  color: "#1e293b",
  padding: "0 0.125rem",
});

const StyledSelect = styled(Select)({
  minWidth: "90px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#fce4ec",
    borderRadius: "0.5rem",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#f382a0",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e91e63",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    padding: "8px 12px",
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "0.5rem",
  },
});

const DaysRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "0.375rem",
});

const DayButton = styled(Box)<{ selected: boolean }>(({ selected }) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "0.8125rem",
  fontWeight: 600,
  transition: "all 0.2s ease",
  backgroundColor: selected ? "#f382a0" : "white",
  color: selected ? "white" : "#94a3b8",
  border: `1px solid ${selected ? "#f382a0" : "#e2e8f0"}`,
  "&:hover": {
    backgroundColor: selected ? "#e91e63" : "#fce4ec",
    borderColor: selected ? "#e91e63" : "#f382a0",
  },
}));

const CancelButton = styled(Button)({
  color: "#64748b",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.875rem",
  padding: "8px 16px",
  borderRadius: "0.5rem",
  "&:hover": { backgroundColor: "#f1f5f9" },
});

const SaveButton = styled(Button)({
  backgroundColor: "#f382a0",
  color: "white",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  padding: "8px 20px",
  borderRadius: "0.5rem",
  boxShadow: "0 2px 8px rgba(243, 130, 160, 0.3)",
  "&:hover": {
    backgroundColor: "#e91e63",
    boxShadow: "0 4px 12px rgba(243, 130, 160, 0.4)",
  },
  "&:disabled": {
    backgroundColor: "#cbd5e1",
    color: "#94a3b8",
  },
});

const CloseButton = styled(Button)({
  backgroundColor: "#f1f5f9",
  color: "#64748b",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.875rem",
  padding: "8px 20px",
  borderRadius: "0.5rem",
  "&:hover": { backgroundColor: "#e2e8f0" },
});

// --- Component ---
const ModCreateSeriesWebsite: React.FC<ModCreateSeriesWebsiteProps> = (props) => {
  const editMode = props.editMode ?? false;
  const viewMode = props.viewMode ?? false;
  const defaultCutoffHour = props.defaultCutoffHour ?? 24;

  // defaultCron คงที่ ไม่ re-create ทุก render
  const defaultCron = useMemo<Partial<CronConfig>>(
    () => ({
      enabled: false,
      days: props.defaultScheduleConfig?.days || ["mon", "tue", "wed", "thu", "fri"],
      hour: props.defaultScheduleConfig?.hour ?? 8,
      minute: props.defaultScheduleConfig?.minute ?? 30,
    }),
    [props.defaultScheduleConfig?.days, props.defaultScheduleConfig?.hour, props.defaultScheduleConfig?.minute]
  );

  // --- State แยก Form กับ Schedule ---
  const [formState, setFormState] = useState<WebsiteFormState>(() =>
    getInitialFormState(editMode, viewMode, props.editSeries, defaultCutoffHour)
  );

  const [scheduleState, setScheduleState] = useState<ScheduleState>(() =>
    getInitialScheduleState(editMode, viewMode, props.editSeries, defaultCron)
  );

  // Reset เมื่อ modal เปิด
  useEffect(() => {
    if (props.open) {
      setFormState(getInitialFormState(editMode, viewMode, props.editSeries, defaultCutoffHour));
      setScheduleState(getInitialScheduleState(editMode, viewMode, props.editSeries, defaultCron));
    }
  }, [props.open]);

  // --- Form Handlers ---
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setFormState((prev) => ({ ...prev, name: value }));
  };

  const handleRssUrlsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;

    setFormState((prev) => ({ ...prev, rss_urls: value }));
  };

  const handleCutoffHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = parseInt(raw, 10);

    setFormState((prev) => ({
      ...prev,
      cutoff_hours: isNaN(parsed) ? 0 : Math.max(0, Math.min(168, parsed)),
    }));
  };

  // --- Schedule Handlers ---
  const handleAutoRepeatToggle = () => {
    setScheduleState((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleDayToggle = (day: Days) => {
    setScheduleState((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  const handleHourChange = (value: string) => {
    setScheduleState((prev) => ({ ...prev, hour: parseInt(value, 10) }));
  };

  const handleMinuteChange = (value: string) => {
    setScheduleState((prev) => ({ ...prev, minute: parseInt(value, 10) }));
  };

  // --- Save: รวม formState + scheduleState ---
  const handleSave = () => {
    const urls = formState.rss_urls
      .split("\n")
      .map((u) => u.trim())
      .filter((u) => u.length > 0);

    const cfg: ScheduleConfig = { rss_urls: urls };

    if (formState.cutoff_hours > 0) {
      cfg.cutoff_days = formState.cutoff_hours / 24;
    }

    if (scheduleState.enabled) {
      cfg.cron = {
        enabled: true,
        days: scheduleState.days,
        hour: scheduleState.hour,
        minute: scheduleState.minute,
      };
    }

    const payload: SeriesSerializer = {
      project_id: props.projectId,
      name: formState.name,
      series_type: formState.series_type,
      schedule_config: cfg,
    };

    if (editMode && formState.id) {
      payload.id = formState.id;
    }

    props.onSave(payload);
  };

  const getTitle = () => {
    if (viewMode) return "Playlist Details (RSS Website)";
    if (editMode) return "Edit Playlist (RSS Website)";
    return "Create Playlist (RSS Website)";
  };

  const isFormValid = formState.name.trim().length > 0;

  const hourValue = scheduleState.hour.toString().padStart(2, "0");
  const minuteValue = scheduleState.minute.toString().padStart(2, "0");

  return (
    <StyledDialog id="megen-mod-series-website-container" open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle id="megen-mod-series-website-header">
        <TitleText id="megen-mod-series-website-title">{getTitle()}</TitleText>
        <IconButton id="megen-mod-series-website-close-btn" size="small" onClick={props.onClose} sx={{ color: "#94a3b8" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent id="megen-mod-series-website-content">
        <FormSection>
          {/* Playlist Name */}
          <Box>
            <FormLabel>Playlist Name</FormLabel>
            <StyledTextField
              id="megen-mod-series-website-name-input"
              fullWidth
              placeholder="Name of Playlist"
              value={formState.name}
              onChange={handleNameChange}
              disabled={viewMode}
              InputProps={{ readOnly: viewMode }}
            />
          </Box>

          {/* RSS URLs */}
          <Box>
            <FormLabel>Add Website [RSS Feed] (One link per line)</FormLabel>
            <StyledTextArea
              id="megen-mod-series-website-rss-input"
              fullWidth
              multiline
              rows={4}
              placeholder={"https://example.com/rss\nhttps://another.com/feed"}
              value={formState.rss_urls}
              onChange={handleRssUrlsChange}
              disabled={viewMode}
              InputProps={{ readOnly: viewMode }}
            />
          </Box>

          {/* Auto Repeat Section */}
          {!viewMode ? (
            <Box>
              <CheckboxLabel
                control={
                  <Checkbox
                    checked={scheduleState.enabled}
                    onChange={handleAutoRepeatToggle}
                    size="small"
                  />
                }
                label="Auto repeat"
              />

              {/* Inline Schedule Settings — แสดงเมื่อ enabled */}
              {scheduleState.enabled && (
                <ScheduleInlineBox>
                  {/* Run Time */}
                  <Box>
                    <ScheduleSectionLabel>Run Time</ScheduleSectionLabel>
                    <TimeRow>
                      <FormControl size="small">
                        <StyledSelect
                          value={hourValue}
                          onChange={(e) => handleHourChange(e.target.value as string)}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                        >
                          {HOUR_OPTIONS.map((h) => (
                            <MenuItem key={h} value={h} sx={{ fontSize: "0.875rem" }}>
                              {h}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>

                      <TimeSeparator>:</TimeSeparator>

                      <FormControl size="small">
                        <StyledSelect
                          value={minuteValue}
                          onChange={(e) => handleMinuteChange(e.target.value as string)}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                        >
                          {MINUTE_OPTIONS.map((m) => (
                            <MenuItem key={m} value={m} sx={{ fontSize: "0.875rem" }}>
                              {m}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                      </FormControl>
                    </TimeRow>
                  </Box>

                  {/* Period Set */}
                  <Box>
                    <ScheduleSectionLabel>Period Set</ScheduleSectionLabel>
                    <PeriodSetRow>
                      <PeriodInput
                        placeholder="24"
                        value={formState.cutoff_hours || ""}
                        onChange={handleCutoffHoursChange}
                        type="number"
                        inputProps={{ min: 1, max: 168 }}
                      />
                      <Typography sx={{ fontSize: "0.875rem", color: "#1e293b" }}>hr.</Typography>
                    </PeriodSetRow>
                  </Box>

                  {/* Run Days */}
                  <Box>
                    <ScheduleSectionLabel>Run Days</ScheduleSectionLabel>
                    <DaysRow>
                      {DAYS_OPTIONS.map((day) => (
                        <DayButton
                          key={day.value}
                          selected={scheduleState.days.includes(day.value)}
                          onClick={() => handleDayToggle(day.value)}
                        >
                          {day.label}
                        </DayButton>
                      ))}
                    </DaysRow>
                  </Box>
                </ScheduleInlineBox>
              )}
            </Box>
          ) : (
            /* View Mode: แสดง Schedule แบบ read-only */
            <Box>
              <FormLabel>Auto Repeat</FormLabel>
              <Typography sx={{ fontSize: "0.875rem", color: "#334155" }}>
                {scheduleState.enabled ? "Enabled" : "Disabled"}
              </Typography>
              {scheduleState.enabled && (
                <Typography sx={{ fontSize: "0.8rem", color: "#64748b", marginTop: "0.25rem" }}>
                  Days: {scheduleState.days.join(", ").toUpperCase() || "-"} |{" "}
                  Time: {hourValue}:{minuteValue}
                  Period: {formState.cutoff_hours} hr.
                </Typography>
              )}
            </Box>
          )}

          {/* Notes */}
          {!viewMode && (
            <NotesList>
              <NoteItem>หากต้องการเพิ่ม URL หลายรายการ ให้ขึ้นบรรทัดใหม่แต่ละ URL</NoteItem>
              <NoteItem>ปัจจุบันระบบรองรับเฉพาะเว็บไซต์ประเภท RSS Feed</NoteItem>
              <NoteItem>แต่ละ URL ระบบดึงข้อมูลได้สูงสุด 50 ข่าว</NoteItem>
            </NotesList>
          )}
        </FormSection>
      </StyledDialogContent>

      <StyledDialogActions id="megen-mod-series-website-footer">
        {viewMode ? (
          <CloseButton id="megen-mod-series-website-close-view-btn" onClick={props.onClose}>Close</CloseButton>
        ) : (
          <>
            <CancelButton id="megen-mod-series-website-cancel-btn" onClick={props.onClose}>Cancel</CancelButton>
            <SaveButton
              id="megen-mod-series-website-submit-btn"
              startIcon={<SaveIcon fontSize="small" />}
              onClick={handleSave}
              disabled={!isFormValid}
            >
              {editMode ? "Update" : "Create"}
            </SaveButton>
          </>
        )}
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ModCreateSeriesWebsite;